import { RowTransactionDto, RowTripTicketDto, TripTicketReportDto, TripTicketTotalReportDto } from "@/dtos";
import { Sector, TripTicket, Vehicle } from "@/entities";
import { getPaymentMethodTypeOption } from "@/helpers/options";
import { formatToCurrency, parseToLocalDateTimeFromUtc } from "@/helpers/utilities";
import { generateColor } from "@/helpers/utilities/colors";
import { BarDataset } from "@/ui/components/graphs/bar/interfaces";

export const formatRowTripTicketDto = (tripTickets: TripTicket[], sectors?: Sector[], vehicles?: Vehicle[]): RowTripTicketDto[] => {
    return tripTickets.map(x => {
        const sectorOrigin = sectors ? sectors.find(s => s.id == x.sector_id_origin)?.name : x.details?.sector_origin;
        const sectorDestination = sectors ? sectors.find(s => s.id == x.sector_id_destination)?.name : x.details?.sector_destination;
        const vehicle = vehicles ? vehicles.find(v => v.id == x.vehicle_id)?.name : x.details.vehicle;

        const row: RowTripTicketDto = {
            ...x,
            paid: x.is_paid ? 'Si' : 'No',
            sector_origin: sectorOrigin ?? 'S/N',
            sector_destination: sectorDestination ?? 'S/N',
            trip_pattern: x.details?.trip_pattern ?? 'S/N',
            vehicle: vehicle ?? 'S/N',
            date_time: parseToLocalDateTimeFromUtc(x.date + " " + x.hour),
            fare_label: x.fare.name,
            amount_label: x.amont.toString(),
            value_label: formatToCurrency(x.value),
            active: x.is_active ? 'Activo' : 'Inactivo',
            latitude: x.coordinates.latitude,
            longitude: x.coordinates.longitude,
            date_time_created: parseToLocalDateTimeFromUtc(x.created_at.toString()),
            status: x.is_paid ? 'Si' : 'No',
            is_status: x.is_paid,
            fare_type_label: x.subfare.fare_type.name
        }

        return row;
    });
}

export const formatGeneralReport = (tripTicketDtos: TripTicketReportDto[], labels: string[]): TripTicketTotalReportDto[] => {
    const result = [] as TripTicketTotalReportDto[];
    for (const label of labels) {
        let totalAmount = 0;
        let totalValue = 0;
        for (const tripTicketDto of tripTicketDtos) {
            const subfare = tripTicketDto.subfares.find(x => x.subfare == label);
            console.log(subfare);
            totalAmount += parseInt(subfare?.amount ?? 0 as any);
            totalValue += parseFloat(subfare?.value ?? '0');
        }


        const totalDto: TripTicketTotalReportDto = {
            subfare: label,
            amount: totalAmount,
            value: totalValue.toFixed(2)
        }

        result.push(totalDto);
    }

    return result;
}

export const formatLabelsGeneralGraph = (tripTicketDtos: TripTicketReportDto[]): string[] => {
    const result: string[] = [];
    tripTicketDtos.map(x => x.subfares.map(y => {
        if (!result.some(label => label == y.subfare)) {
            result.push(y.subfare);
        }
    }));

    return result;
}

export const formatDatasetsGeneralAmountGraph = (tripTicketDtos: TripTicketReportDto[], labels: string[]): BarDataset[] => {
    const barDatasets = [] as BarDataset[];
    for (const tripTicketDto of tripTicketDtos) {
        const amounts = [] as number[];

        for (const label of labels) {
            const subfare = tripTicketDto.subfares.find(x => x.subfare == label);
            amounts.push(subfare?.amount ?? 0);
        }

        const colorAmount = generateColor();
        const amountBarDataset: BarDataset = {
            label: `${tripTicketDto.fare}`,
            data: amounts,
            backgroundColor: [colorAmount.color]
        }
        barDatasets.push(amountBarDataset);
    }

    return barDatasets;
}

export const formatDatasetsGeneralValuesGraph = (tripTicketDtos: TripTicketReportDto[], labels: string[]): BarDataset[] => {
    const barDatasets = [] as BarDataset[];
    for (const tripTicketDto of tripTicketDtos) {
        const values = [] as number[];

        for (const label of labels) {
            const subfare = tripTicketDto.subfares.find(x => x.subfare == label);
            values.push(parseFloat(subfare?.value ?? '0'));
        }

        const colorValue = generateColor();
        const valueBarDataset: BarDataset = {
            label: `${tripTicketDto.fare}`,
            data: values,
            backgroundColor: [colorValue.color]
        }
        barDatasets.push(valueBarDataset);
    }

    return barDatasets;
}



export const formatLabelsGraph = (tripTicketDtos: TripTicketReportDto[], fare_id?: number): string[] => {
    const result: string[] = [];
    if (fare_id) {
        const dtos = tripTicketDtos.filter(x => x.fare_id == fare_id);
        dtos.map(x => x.subfares.map(y => result.push(y.subfare)));
        return result;
    }

    tripTicketDtos.map(x => x.subfares.map(y => result.push(y.subfare)));
    return result;
}

export const formatDatasetsGraph = (tripTicketDtos: TripTicketReportDto[], fare_id?: number): BarDataset[] => {
    const barDatasets = [] as BarDataset[];
    const values = [] as number[];
    const amounts = [] as number[];

    const data = (fare_id) ? tripTicketDtos.filter(x => x.fare_id == fare_id) : tripTicketDtos;
    data.map(x => x.subfares.map(y => {
        amounts.push(y.amount);
        values.push(parseFloat(y.value));
    }));

    const colorAmount = generateColor();
    const amountBarDataset: BarDataset = {
        label: 'Cantidad',
        data: amounts,
        backgroundColor: [colorAmount.color]
    }
    barDatasets.push(amountBarDataset);

    const colorMoney = generateColor();
    const valueBarDataset: BarDataset = {
        label: 'Dinero',
        data: values,
        backgroundColor: [colorMoney.color]
    }
    barDatasets.push(valueBarDataset);

    return barDatasets;
}

export const formatTransactionDto = (tripTickets: TripTicket[], vehicles: Vehicle[]): RowTransactionDto[] => {
    return tripTickets.map(x => {
        const vehicle = vehicles.find(v => v.id == x.vehicle_id);
        const row: RowTransactionDto = {
            ...x,
            passenger: x.payment_method?.passenger ? x.payment_method.passenger?.first_name + " " + x.payment_method.passenger?.last_name : 'S/N',
            payment_method_identificative: x.payment_method?.identificative ?? 'S/N',
            payment_method_type: getPaymentMethodTypeOption(x.payment_method.type).label,
            brand: x.payment_method.brand?.name ?? 'S/N',
            fare_type: x.payment_method?.fare_type?.name ?? 'S/N',
            vehicle: vehicle?.name ?? '',
            amount_label: `$ ${formatToCurrency(x.value)}`,
            balance_label: `$ ${formatToCurrency(x.balance)}`,
            datetime: parseToLocalDateTimeFromUtc(x.date + " " + x.hour),
            equipment: x.device_details?.ip_equipment ?? 'S/N',
            latitude: x.coordinates?.latitude?.toString(),
            longitude: x.coordinates?.longitude?.toString(),
        }

        console.log('Transaccion', x);
        return row;
    });
}